// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';

import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider, type CurrentUser } from '@/lib/auth';
import { makeQueryClient } from '@/lib/queryClient';
import { GreenlightRoutes } from '@/router';

export interface AppProps {
  initialUser?: CurrentUser | null;
  router?: 'browser' | 'memory';
  initialEntries?: string[];
  queryClient?: QueryClient;
}

export function App({
  initialUser,
  router = 'browser',
  initialEntries,
  queryClient,
}: AppProps = {}) {
  const client = React.useMemo(() => queryClient ?? makeQueryClient(), [queryClient]);
  const RouterComponent = router === 'memory' ? MemoryRouter : BrowserRouter;
  const routerProps = {
    future: { v7_startTransition: true, v7_relativeSplatPath: true },
    ...(router === 'memory' && initialEntries ? { initialEntries } : {}),
  };

  return (
    <ThemeProvider defaultTheme="system">
      <QueryClientProvider client={client}>
        <AuthProvider initialUser={initialUser}>
          <TooltipProvider>
            <RouterComponent {...routerProps}>
              <GreenlightRoutes />
            </RouterComponent>
            {/* Top-center so toasts clear the right inspect slide-over's footer
                actions and the mobile bottom sheet (both sit bottom-right). */}
            <Toaster position="top-center" />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
